import { atom } from 'nanostores';

import type { HintAnchorId } from './types';

/**
 * Mounted DOM anchors keyed by hint-anchor id. Insertion order is preserved
 * so we can deterministically spotlight the first registered instance.
 *
 * Not a nanostore — subscribers consume {@link $anchorTick} for change
 * notifications and read through the getters. Keeps the ref-callback hot
 * path cheap.
 */
const anchors = new Map<HintAnchorId, HTMLElement[]>();

/** Monotonic change counter; bumps on every register/unregister. */
export const $anchorTick = atom<number>(0);

function bumpTick(): void {
  $anchorTick.set($anchorTick.get() + 1);
}

/** Idempotent. @internal Engine-only; features call {@link useHintAnchor}. */
export function registerAnchor(id: HintAnchorId, element: HTMLElement): void {
  const list = anchors.get(id);
  if (list === undefined) {
    anchors.set(id, [element]);
    bumpTick();
    return;
  }
  if (list.includes(element)) return;
  list.push(element);
  bumpTick();
}

/** No-op if not registered. @internal Engine-only. */
export function unregisterAnchor(id: HintAnchorId, element: HTMLElement): void {
  const list = anchors.get(id);
  if (list === undefined) return;
  const index = list.indexOf(element);
  if (index === -1) return;
  list.splice(index, 1);
  if (list.length === 0) anchors.delete(id);
  bumpTick();
}

/** First-registered element under `id`, or `null`. */
export function getFirstAnchor(id: HintAnchorId): HTMLElement | null {
  const list = anchors.get(id);
  if (list === undefined || list.length === 0) return null;
  return list[0] ?? null;
}

export function hasAnchor(id: HintAnchorId): boolean {
  return anchors.has(id);
}
